<template>
  <!-- Backdrop for click-outside -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-40"
    @click="$emit('close')"
  ></div>

  <div
    v-if="isOpen"
    ref="panel"
    role="dialog"
    aria-modal="true"
    aria-label="Release Notes"
    :class="[
      'fixed top-[52px] right-0 bottom-0 bg-white shadow-[0px_0px_100px_0px_rgba(0,0,0,0.2)] border border-[#E8E9E9] rounded-tl-lg z-50 flex flex-col overflow-hidden transition-all duration-200',
      isExpanded ? 'w-[900px]' : 'w-[534px]'
    ]"
  >
    <!-- Header -->
    <div class="px-10 py-5">
      <div class="flex items-start justify-between">
        <!-- Left side: Label and Title -->
        <div>
          <p class="text-[11px] text-[#626264] leading-none mb-2">Release Notes</p>
          <!-- Release selector dropdown -->
          <select
            v-if="releases.length > 0"
            v-model="selectedReleaseDate"
            class="text-xl font-semibold text-[#1E1F21] bg-transparent border-none cursor-pointer hover:text-[#353537] focus:outline-none focus:ring-2 focus:ring-[#4573D2] focus:ring-offset-1 p-0 pr-5 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%23626264%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%20011.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[length:14px] bg-[right_0_center]"
          >
            <option
              v-for="release in releases"
              :key="release.date"
              :value="release.date"
            >
              {{ release.title }}
            </option>
          </select>
          <h2 v-else class="text-xl font-semibold text-[#1E1F21] leading-none">
            No releases
          </h2>
        </div>

        <!-- Right side: Icons -->
        <div class="flex items-center gap-4">
          <button
            @click="isExpanded = !isExpanded"
            class="text-[#353537] hover:text-[#1E1F21] transition-colors focus:outline-none focus:ring-2 focus:ring-[#4573D2] focus:ring-offset-1 rounded"
            :title="isExpanded ? 'Collapse panel' : 'Expand panel'"
          >
            <font-awesome-icon :icon="isExpanded ? 'down-left-and-up-right-to-center' : 'up-right-and-down-left-from-center'" class="w-3.5 h-3.5" />
          </button>
          <button
            @click="$emit('close')"
            class="text-[#353537] hover:text-[#1E1F21] transition-colors focus:outline-none focus:ring-2 focus:ring-[#4573D2] focus:ring-offset-1 rounded"
            title="Close panel"
          >
            <font-awesome-icon icon="arrow-right-from-bracket" class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Divider -->
    <div class="w-full h-px bg-[#E8E9E9]"></div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-10 pt-4 pb-5">
      <!-- Loading state -->
      <div v-if="isLoading && !content" class="flex items-center justify-center h-32">
        <div class="text-[#626264] text-sm">Loading release notes...</div>
      </div>

      <!-- Error state -->
      <div v-else-if="error && !releases.length" class="flex items-center justify-center h-32">
        <div class="text-[#D53D3D] text-sm">{{ error }}</div>
      </div>

      <!-- Content -->
      <div
        v-else
        class="prose prose-sm max-w-none prose-headings:text-[#1E1F21] prose-h1:hidden prose-h2:text-base prose-h2:font-semibold prose-h2:mt-5 prose-h2:mb-2 first:prose-h2:mt-0 prose-h3:text-sm prose-h3:font-medium prose-h3:mt-4 prose-h3:mb-2 prose-p:text-[#353537] prose-p:text-sm prose-ul:my-2 prose-ul:text-sm prose-li:my-0.5 prose-li:text-[#353537]"
        v-html="renderedContent"
      ></div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'

export default {
  name: 'ReleaseNotesPanel',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      releases: [],
      selectedReleaseDate: '',
      contentCache: {}, // Cache fetched content by date
      isExpanded: false,
      isLoading: false,
      error: null,
    }
  },
  computed: {
    selectedRelease() {
      return this.releases.find(r => r.date === this.selectedReleaseDate)
    },
    content() {
      return this.contentCache[this.selectedReleaseDate] || ''
    },
    renderedContent() {
      if (!this.content) return ''
      return DOMPurify.sanitize(marked(this.content))
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.fetchManifest();
        document.addEventListener('keydown', this.handleKeydown)
      } else {
        document.removeEventListener('keydown', this.handleKeydown)
      }
    },
    selectedReleaseDate(date) {
      if (date && !this.contentCache[date]) {
        this.fetchContent(date)
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('close')
      }
    },
    async fetchManifest() {
      this.error = null
      this.isLoading = true
      try {
        const response = await this.$axios.get('/api/document-library/download/', {
          params: { key: 'GENERAL/release-notes/manifest.json', content: 'true' },
        })
        const manifest = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
        this.releases = manifest.releases || []
        if (this.releases.length) {
          this.selectedReleaseDate = this.releases[0].date
          // Pre-fetch first release content
          this.fetchContent(this.releases[0].date)
        }
      } catch (e) {
        console.error('Failed to load release notes manifest:', e)
        this.error = 'Failed to load release notes'
      }
      this.isLoading = false
    },
    async fetchContent(date) {
      if (this.contentCache[date]) return // Already cached
      this.isLoading = true
      try {
        const response = await this.$axios.get('/api/document-library/download/', {
          params: { key: `GENERAL/release-notes/${date}.md`, content: 'true' },
        })
        this.contentCache[date] = response.data
      } catch (e) {
        console.error('Failed to load release notes content:', e)
        this.contentCache[date] = '# Error\n\nFailed to load release notes for this date.'
      }
      this.isLoading = false
    },
  },
}
</script>
